import { Typography } from '@mui/material';
import { CarcinogenicReportWorker } from '../../types';
import { WorkerInfoCard } from '../WorkerInfoCard';
import useMultipleWorkerSubstancesForms from '../../hooks/useMultipleWorkerSubstancesForm';
import { EXPOSITION_OPTIONS } from '../../consts';
import AutocompleteComponent from '../../../../../../components/layout/Autocomplete';
import { DrawerSvccCargando } from '../../../../components/drawer/DrawerSvcc';
import { TEST_IDS } from '../../testIds';
import {
    COLORES_DRAWER,
    DrawerSvccCuerpo,
    DrawerSvccEncabezado,
    DrawerSvccPie,
    DrawerSvccSeccion
} from '../../../../components/drawer/DrawerSvcc';
import CamposSvcc from '../../../../components/drawer/CamposSvcc';

interface WorkerSubstancesAndMedicalExamsDrawerProps {
    selectedWorker: CarcinogenicReportWorker | null;
    translate: (key: string) => string;
    handleCloseDrawer: () => void;
    onlyVisual: boolean;
}

/**
 * Los errores del hook vienen con la clave `${indice}-${campo}`; CamposSvcc
 * los espera por `campo`. Se recortan los de un formulario sin tocarlos.
 */
const erroresDelFormulario = (errors: Record<string, any>, formIndex: number) => {
    const prefijo = `${formIndex}-`;
    return Object.fromEntries(
        Object.entries(errors || {})
            .filter(([clave]) => clave.startsWith(prefijo))
            .map(([clave, valor]) => [clave.slice(prefijo.length), valor])
    );
};

const WorkerSubstancesAndMedicalExamsDrawer = ({
    handleCloseDrawer,
    selectedWorker,
    translate,
    onlyVisual
}: WorkerSubstancesAndMedicalExamsDrawerProps) => {
    const {
        formsData,
        handleSubmit,
        formConfigs,
        handleChange,
        errors,
        expositionType,
        handleExpositionTypeChange,
        isFetchingWorkerDetail,
        isEditingWorkerDetail,
        isExposed
    } = useMultipleWorkerSubstancesForms(selectedWorker, handleCloseDrawer);

    return (
        <>
            <DrawerSvccEncabezado
                titulo={translate('workerForm.title')}
                paso={translate('workerForm.drawerPaso')}
                onCerrar={handleCloseDrawer}
            />
            <DrawerSvccCuerpo>
                <WorkerInfoCard
                    name={selectedWorker?.nombre || ''}
                    surname={selectedWorker?.apellido || ''}
                    cuil={selectedWorker?.cuil || '-'}
                    workPosition={selectedWorker?.puestoTrabajo?.nombre || '-'}
                    sector={selectedWorker?.sector?.nombre || '-'}
                    handleDeleteClick={() => {}}
                    translate={translate}
                />

                {!isExposed && (
                    <DrawerSvccSeccion>
                        <Typography
                            variant="subtitle1"
                            textAlign="center"
                            sx={{ color: COLORES_DRAWER.textoSuave }}
                        >
                            {translate('workerForm.noExposed')}
                        </Typography>
                    </DrawerSvccSeccion>
                )}

                {isExposed && (
                    <DrawerSvccSeccion titulo={translate('workerForm.secciones.exposicion')}>
                        <AutocompleteComponent
                            options={EXPOSITION_OPTIONS}
                            onlyVisual={onlyVisual}
                            value={expositionType}
                            onChange={(e: any) => handleExpositionTypeChange(e)}
                            templateLabel={(item: any) => item?.descripcion}
                            label={'Tipo de Exposición'}
                            // Como el resto de los campos del drawer: en `small`
                            // el input queda más alto que lo previsto y la
                            // etiqueta vacía cae sobre el borde (se veía tachada).
                            size="medium"
                            fullWidth
                            testID={TEST_IDS.editSubstancesAndMedicalExams.expositionType}
                        />
                    </DrawerSvccSeccion>
                )}

                {isExposed &&
                    formsData.map((formData: any, formIndex) => (
                        <DrawerSvccSeccion
                            key={formIndex}
                            titulo={`${formData.substanceCode} - ${formData.substanceDescription}`}
                        >
                            <CamposSvcc
                                config={formConfigs[formIndex] || []}
                                formData={formData}
                                errors={erroresDelFormulario(errors, formIndex)}
                                onChange={(name, value) => handleChange(formIndex, name, value)}
                                onlyVisual={onlyVisual}
                            />
                        </DrawerSvccSeccion>
                    ))}
            </DrawerSvccCuerpo>
            <DrawerSvccPie
                onCancelar={handleCloseDrawer}
                onConfirmar={handleSubmit}
                textoCancelar={translate('workerForm.buttons.cancel')}
                textoConfirmar={translate('workerForm.buttons.save')}
                nota={
                    onlyVisual || !isExposed
                        ? undefined
                        : translate('workerForm.camposObligatorios')
                }
                cargando={isEditingWorkerDetail}
                sinConfirmar={onlyVisual}
            />
            <DrawerSvccCargando cargando={isFetchingWorkerDetail} />
        </>
    );
};

export { WorkerSubstancesAndMedicalExamsDrawer };
